import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '@apollo/react-hooks';
import { WatchQueryFetchPolicy } from 'apollo-client';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { Widget } from '@spins/react-ui-library';
import {
  ACCOUNTS_TYPE,
  STORES_TYPE,
  ITEMS_TYPE,
  GlobalFilterType,
  PeriodEndWeekType,
  CampaignObjectiveKeyLabel,
} from 'utils/types';
import { periodEndWeekSelector } from 'containers/AccessControl/selectors';
import * as globalSelectors from 'store/global/selectors';
import SalesHighlightsWidget from 'components/SalesHighlightsWidget';
import TablesWithTabs from 'components/TablesWithTabsWidget';
import _ from 'lodash';
import { getData } from 'utils/functions';
import { campaignsClient } from 'apollo';
import {
  GetCampaignQuery as result,
  GetCampaignQueryVariables as vars,
  GetCampaignDocument as query,
} from 'generated/campaigns-graphql';
import DataPeriodSelect from 'components/DataPeriodSelect';
import CampaignsPageHeader from 'components/Campaigns/PageHeader';
import * as createEditActions from 'containers/Campaigns/CreateEdit/store/actions';
import './styles.scss';
import CreateEditCampaignModal from 'containers/Campaigns/CreateEdit';
import { CampaignUpdateActionContext } from 'utils/campaigns';
import * as pageHeaderSelectors from 'containers/PageHeader/selectors';
import * as pageHeaderActions from 'containers/PageHeader/actions';
import Formats from 'utils/formats';
import { Button } from 'react-bootstrap';
import { OTHER_ROUTES } from 'utils/routes';
import ReactGA from 'react-ga';
import TrendChartWidget from 'components/Charts/Widgets/TrendChartWidget';
import DistributionChartWidget from 'components/Charts/Widgets/DistributionChartWidget';
import LoadingSpinner from 'components/LoadingSpinner';
import { useCampaignsState } from 'providers/campaigns';

interface Props {
  location: any;
}

const CampaignDetails: React.FC<Props> = ({ location }) => {
  const [selectedTab, setSelectedTab] = useState(ACCOUNTS_TYPE);
  const [campaignArray, setCampaignArray] = useState([]);
  const [filter, setFilter] = useState<GlobalFilterType>(null);
  const { refetchFunctions, setRefetchFunctions } = useCampaignsState();

  const dispatch = useDispatch();
  const clientId = useSelector<string>(globalSelectors.clientIdSelector);
  const currentBrand = useSelector<string>(globalSelectors.currentBrandSelector);
  const periodEndWeek = useSelector<PeriodEndWeekType>(periodEndWeekSelector);
  const currentWeeks = useSelector<number>(pageHeaderSelectors.globalFilterWeeksSelector);
  const isEnterpriseContext = useSelector<boolean>(globalSelectors.isEnterpriseContextSelector);

  useEffect(() => {
    ReactGA.pageview(OTHER_ROUTES.CAMPAIGN_DETAILS);
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const calculateFilter = (campaigns: Array<any>): GlobalFilterType => {
    return campaigns.reduce(
      (accumulator, currentCampaign) => {
        return {
          ...accumulator,
          accounts: _.uniq([...accumulator.accounts].concat(currentCampaign.storeFilters.accounts)),
          stores: _.uniq([...accumulator.stores].concat(currentCampaign.storeFilters.stores)),
          states: _.uniq([...accumulator.states].concat(currentCampaign.storeFilters.states)),
          items: _.uniq([...accumulator.items].concat(currentCampaign.itemFilters.items)),
        };
      },
      {
        weeks: currentWeeks,
        endWeek: periodEndWeek.spins,
        ...(isEnterpriseContext && { brand: currentBrand }),
        accounts: [],
        stores: [],
        states: [],
        items: [],
      }
    );
  };

  const { data, refetch } = useQuery<result, vars>(query, {
    client: campaignsClient as any,
    variables: { id: location.state.id },
    fetchPolicy: 'network-only' as WatchQueryFetchPolicy,
  });

  useEffect(() => {
    if (data && campaignArray.length === 0) {
      setCampaignArray([getData(data)]);
    }
    if (campaignArray.length > 0 && !filter) {
      setFilter(calculateFilter(campaignArray));
    }
  }, [calculateFilter, campaignArray, data, filter]);

  const refetchAndSet = () => {
    refetch()
      .then((data) => {
        setCampaignArray([getData(data).retrieve]);
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    !!refetch &&
      setRefetchFunctions({
        ...refetchFunctions,
        getCampaign: () => refetchAndSet(),
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch]);

  return campaignArray.length > 0 && filter ? (
    <div className="campaign-details-page">
      <Row>
        <CampaignsPageHeader title={campaignArray[0].name}>
          <div className="header-buttons-container">
            <DataPeriodSelect
              currentWeeks={currentWeeks}
              onSelect={(weeks: number) =>
                dispatch(pageHeaderActions.updateGlobalFilterWeeks(weeks))
              }
            />
          </div>
        </CampaignsPageHeader>
      </Row>
      <Row className="mt-3">
        <Col md={9}>
          <Row>
            <Col>
              <SalesHighlightsWidget
                clientId={clientId}
                periodEndWeek={periodEndWeek}
                currentBrand={currentBrand}
                globalFilter={filter}
                type="accounts-items"
              />
            </Col>
          </Row>
          <Row>
            <Col className="mt-3" lg={12} xl={6}>
              <TrendChartWidget globalFilter={filter} />
            </Col>
            <Col className="mt-3" lg={12} xl={6}>
              <DistributionChartWidget globalFilter={filter} />
            </Col>
          </Row>
          <Row className="mt-3">
            <Col>
              <TablesWithTabs
                clientId={clientId}
                globalFilter={filter}
                selectedTab={selectedTab}
                types={[ACCOUNTS_TYPE, STORES_TYPE, ITEMS_TYPE]}
                onTabSelect={(eventKey) => setSelectedTab(eventKey)}
              />
            </Col>
          </Row>
        </Col>
        <Col md={3}>
          <Row>
            <Col>
              <Widget>
                {{
                  header: {
                    title: 'Campaign Details',
                  },
                  body: {
                    content: (
                      <div className="campaigns-details-data-selection">
                        <div className="details-main">
                          <div>
                            <span className="title">Campaign Name</span>
                            {campaignArray[0].name}
                          </div>
                          <div>
                            <span className="title">Store Group Name</span>
                            {campaignArray[0].storeGroupName}
                          </div>
                          <div>
                            <span className="title">Item Group Name</span>
                            {campaignArray[0].itemGroupName}
                          </div>
                          <div>
                            <span className="title">Campaign Objective</span>
                            {CampaignObjectiveKeyLabel[campaignArray[0].objective]}
                          </div>
                          <div className="dates-container">
                            <div className="start-date">
                              <span className="date-title">Start Date</span>
                              {campaignArray[0].startDate !== null ? (
                                <span>{Formats.date(campaignArray[0].startDate)}</span>
                              ) : (
                                <span />
                              )}
                            </div>
                            <div>
                              <span className="date-title">End Date</span>
                              {campaignArray[0].endDate !== null ? (
                                <span>{Formats.date(campaignArray[0].endDate)}</span>
                              ) : (
                                <span />
                              )}
                            </div>
                          </div>
                          <div>
                            <span className="title">Notes</span>
                            {campaignArray[0].notes}
                          </div>
                          <Button
                            className="btn-edit"
                            onClick={() =>
                              dispatch(
                                createEditActions.openEditCampaignModal(
                                  location.state.id,
                                  CampaignUpdateActionContext.DETAILS
                                )
                              )
                            }
                          >
                            Edit
                          </Button>
                        </div>
                      </div>
                    ),
                  },
                }}
              </Widget>
            </Col>
          </Row>
        </Col>
      </Row>
      <CreateEditCampaignModal />
    </div>
  ) : (
    <LoadingSpinner />
  );
};

export default CampaignDetails;
